import React, { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { useAuth } from '../contexts/AuthContext';
import { familyCodeService } from '../services/familyCodeService';
import { accessRequestService } from '../services/accessRequestService';
import { subscriptionService } from '../services/subscriptionService';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CancelIcon from '@mui/icons-material/Cancel';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

const FamilyCodeManager = () => {
  const { profile, loading: profileLoading } = useProfile();
  const { currentUser } = useAuth();
  const [familyCode, setFamilyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newRole, setNewRole] = useState('member');
  const [pendingRequests, setPendingRequests] = useState([]);

  const loadFamilyMembers = useCallback(async () => {
    try {
      const members = await familyCodeService.getFamilyMembers(profile.familyCode);
      setFamilyMembers(members);
    } catch (err) {
      setError('Failed to load family members');
    }
  }, [profile.familyCode]);

  const loadPendingRequests = useCallback(async () => {
    // Pending requests require knowing which family was requested — skip if no familyCode
    if (!currentUser?.uid || profile?.familyCode) return;
    // Cannot safely query across all families (permissions), so nothing to load yet
    setPendingRequests([]);
  }, [currentUser, profile?.familyCode]);

  useEffect(() => {
    if (profile.familyCode) {
      loadFamilyMembers();
    }
  }, [profile.familyCode, loadFamilyMembers]);

  useEffect(() => {
    loadPendingRequests();
  }, [loadPendingRequests]);

  const handleRoleChange = async (memberId, role) => {
    try {
      setLoading(true);
      setError('');

      // Get the member's document
      const memberRef = doc(db, 'users', memberId);
      const memberDoc = await getDoc(memberRef);
      
      if (!memberDoc.exists()) {
        throw new Error('Member not found');
      }

      // Update the role field
      const isAdmin = role === 'admin' || role === 'owner';
      await updateDoc(memberRef, {
        role: role,
        isAdmin: isAdmin,
        groups: isAdmin ? arrayUnion('ADMIN') : arrayRemove('ADMIN'),
        updatedAt: new Date()
      });

      // Also sync to the member document in families collection
      if (profile?.familyCode) {
        const familyMemberRef = doc(db, 'families', profile.familyCode, 'members', memberId);
        const familyMemberDoc = await getDoc(familyMemberRef);
        if (familyMemberDoc.exists()) {
          await updateDoc(familyMemberRef, {
            role: role,
            isAdmin: isAdmin,
            groups: isAdmin ? arrayUnion('ADMIN') : arrayRemove('ADMIN'),
            updatedAt: new Date()
          });
        }
      }

      setSnackbarMessage('Role updated successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Refresh member list
      await loadFamilyMembers();
    } catch (err) {
      logger.error('Error updating role:', err);
      setSnackbarMessage(err.message || 'Failed to update role');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
      setShowRoleDialog(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this family member?')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await familyCodeService.removeFamilyMember(profile.familyCode, memberId);
      // Release the member's seat back to the pool (no-op if they didn't hold one)
      try {
        await subscriptionService.releaseSeat(profile.familyCode, memberId);
      } catch (seatErr) {
        logger.error('handleRemoveMember: releaseSeat failed (non-fatal):', seatErr);
      }
      setSnackbarMessage('Member removed successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      await loadFamilyMembers();
    } catch (err) {
      logger.error('Error removing member:', err);
      setSnackbarMessage('Error removing member. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFamily = async () => {
    try {
      setLoading(true);
      setError('');
      await familyCodeService.createFamily(currentUser.uid);
      setSnackbarMessage('Family created successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      logger.error('Error creating family:', err);
      setSnackbarMessage('Error creating family. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFamily = async () => {
    try {
      setLoading(true);
      setError('');

      if (!familyCode) {
        throw new Error('Please enter a family code');
      }

      // Create access request instead of directly joining
      await accessRequestService.createFamilyJoinRequest(
        currentUser.uid,
        familyCode,
        currentUser.email,
        currentUser.displayName || profile?.displayName || 'Unknown User'
      );

      setSnackbarMessage('Access request sent successfully. The family admin will review your request.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setFamilyCode('');

      // Reload pending requests to show the new request
      await loadPendingRequests();
    } catch (err) {
      logger.error('Error requesting family access:', err);
      setSnackbarMessage(err.message || 'Error sending access request. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveFamily = async () => {
    try {
      setLoading(true);
      setError('');
      await familyCodeService.leaveFamily(currentUser.uid);
      setSnackbarMessage('Successfully left family');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      logger.error('Error leaving family:', err);
      setSnackbarMessage('Error leaving family. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };


  const handleCopyCode = () => {
    navigator.clipboard.writeText(profile.familyCode);
    setSnackbarMessage('Family code copied to clipboard');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
  };


  const handleOpenRoleDialog = (member) => {
    setSelectedMember(member);
    setNewRole(member.role || 'member');
    setShowRoleDialog(true);
  };

  const handleCloseRoleDialog = () => {
    setShowRoleDialog(false);
    setSelectedMember(null);
    setNewRole('member');
  };

  const handleRoleSubmit = async () => {
    if (selectedMember && newRole) {
      await handleRoleChange(selectedMember.id, newRole);
      handleCloseRoleDialog();
    }
  };

  const handleCancelRequest = async (requestId, familyCode) => {
    if (!window.confirm('Are you sure you want to cancel this access request?')) {
      return;
    }
    try {
      setLoading(true);
      await accessRequestService.cancelRequest(requestId, familyCode);
      setSnackbarMessage('Access request cancelled successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      await loadPendingRequests();
    } catch (err) {
      logger.error('Error cancelling request:', err);
      setSnackbarMessage('Error cancelling request. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const renderFamilyMembers = () => {
    if (!familyMembers.length) {
      return (
        <Typography color="textSecondary">
          No family members found
        </Typography>
      );
    }

    // Check if current user is admin or owner
    // Supports all permission approaches: role field, groups array, and isAdmin boolean
    const isAdminOrOwner = profile?.role === 'admin' ||
      profile?.role === 'owner' ||
      profile?.isAdmin === true ||
      (profile?.groups || []).includes('ADMIN');

    return (
      <List disablePadding>
        {familyMembers.map((member) => (
          <ListItem
            key={member.id}
            secondaryAction={isAdminOrOwner ? (
              <Box sx={{ display: 'flex', gap: 0 }}>
                <Tooltip title="Change role">
                  <IconButton size="small" onClick={() => handleOpenRoleDialog(member)}>
                    <AdminPanelSettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove from family">
                  <IconButton size="small" onClick={() => handleRemoveMember(member.id)}>
                    <PersonRemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : undefined}
          >
            <ListItemText
              primary={member.displayName || member.email}
              secondary={`Role: ${member.role || 'member'}`}
              primaryTypographyProps={{ noWrap: true }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  if (profileLoading) {
    return <CircularProgress />;
  }

  return (
    <Box data-testid="family-code-manager" sx={{ mt: 2 }}>
      {loading && <CircularProgress />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {!profile.familyCode ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Join or Create a Family</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Family Code"
              value={familyCode}
              onChange={(e) => setFamilyCode(e.target.value)}
              placeholder="Enter family code"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              onClick={handleJoinFamily}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Request to Join Family
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateFamily}
              disabled={loading}
            >
              Create New Family
            </Button>
          </Grid>

          {/* Pending Requests Section */}
          {pendingRequests.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Pending Access Requests
                </Typography>
                <List>
                  {pendingRequests.map((request) => (
                    <ListItem
                      key={request.id}
                      sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}
                      secondaryAction={
                        <Tooltip title="Cancel request">
                          <IconButton
                            edge="end"
                            onClick={() => handleCancelRequest(request.id, request.familyCode)}
                            disabled={loading}
                            size="small"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemText
                        primary={`Family: ${request.familyName}`}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.secondary">
                              Requested on: {request.createdAt?.toDate ? new Date(request.createdAt.toDate()).toLocaleDateString() : 'Unknown'}
                            </Typography>
                            <br />
                            <Chip
                              label="Pending"
                              size="small"
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </>
          )}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Family Management</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>
                Family Code: {profile.familyCode}
              </Typography>
              <Tooltip title="Copy family code">
                <IconButton onClick={handleCopyCode} size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleLeaveFamily}
                disabled={loading}
              >
                Leave Family
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              To invite family members, use the "Invite Family Members" section above with relationship options.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {renderFamilyMembers()}
          </Grid>
        </Grid>
      )}

      {/* Add Role Change Dialog */}
      <Dialog open={showRoleDialog} onClose={handleCloseRoleDialog}>
        <DialogTitle>Change Member Role</DialogTitle>
        <DialogContent>
          <Select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            fullWidth
            margin="dense"
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="owner">Owner</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleDialog}>Cancel</Button>
          <Button onClick={handleRoleSubmit} color="primary">
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FamilyCodeManager; 